import type { Result } from '../types/result';
import {
  interpolateCtaUrl,
  type CtaInterpolationSources,
} from './interpolateCtaUrl';

export type ApplicationCtaSource = Partial<
  Pick<
    Result,
    | 'applicationCtaURL'
    | 'applicationCtaFieldMap'
    | 'applicationCtaStaticParams'
    | 'applicationCtaMissingValue'
  >
>;

/**
 * Resolves the href every "Apply" surface (ExitStrategy drawer, Recommender
 * modal, post-submit Card "Next Steps") opens on click.
 *
 * When the result carries no `applicationCtaFieldMap`/`staticParams`
 * (the common case — a plain offering `application_cta` with no org
 * fallback map) this is a pass-through to `applicationCtaURL`, so every
 * surface's pre-EDUN-14483 behavior is unchanged. When a map is present,
 * runs it through `interpolateCtaUrl` against the caller's lead /
 * partnerIdentifiers sources (EDUN-14483). Returns `undefined` when there
 * is no base URL to open, when the base URL isn't a valid http(s) URL, or
 * when `missingValue: 'hide'` resolves to hide.
 */
export function resolveApplicationCtaHref(
  result: ApplicationCtaSource | undefined,
  sources: CtaInterpolationSources,
): string | undefined {
  const baseUrl = result?.applicationCtaURL;
  if (!baseUrl) return undefined;

  const { applicationCtaFieldMap, applicationCtaStaticParams } = result ?? {};
  if (!applicationCtaFieldMap && !applicationCtaStaticParams) {
    return baseUrl;
  }

  const resolved = interpolateCtaUrl({
    baseUrl,
    fieldMap: applicationCtaFieldMap,
    staticParams: applicationCtaStaticParams,
    missingValue: result?.applicationCtaMissingValue,
    sources,
  });

  return 'href' in resolved ? resolved.href : undefined;
}
